import React, { useEffect } from "react";
import { Row, Col, Tooltip } from "antd";
import { useGetQAAlertsSummaryQuery } from "../../../Redux/service/Consultant/ConsultantDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";

export default function QAAlertsSummary({
	refreshKey,
	fromDate,
	toDate,
	type,
	projectId,
}) {
	const {
		data: overviewData,
		isLoading,
		refetch,
	} = useGetQAAlertsSummaryQuery(
		{
			// fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
			// toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
			// type,
			// projectId,
		},
		{ refetchOnMountOrArgChange: true }
	);

	useEffect(() => {
		if (refreshKey > 0) {
			refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshKey]);

	if (isLoading) {
		return <Loader />;
	}

	const data = overviewData?.data || overviewData || {};

	// Icon paths - user will replace these with actual icons
	const iconBasePath = `${process.env.PUBLIC_URL}/assets/qa-dashboard-icons/`;
const formatDaysToPretty = (days) => {
	const totalHours = Math.round(days * 24);
	const d = Math.floor(totalHours / 24);
	const h = totalHours % 24;
	let result = "";

	if (d > 0) result += `${d} ${d === 1 ? "day" : "days"}`;
	if (h > 0) result += `${result ? " • " : ""}${h} ${h === 1 ? "hour" : "hours"}`;

	return result || "0";
};

	const alertTypes = [
		{
			key: "delayedApprovalsCount",
			title: trans("Delayed Approvals Count"),
			icon: `${iconBasePath}hourglass.svg`,
			iconColor: "#023E8A", // Dark blue
			tooltip: trans(
				`Items should be approved within these timeframes. For IR and MIR, QA within 2 days and Consultant within 3 days. For SNAG, QA within 1 day and Consultant within 2 days. For Submittals, QA within 3 days and Consultant within 5 days. Anything beyond these limits is considered delayed.`
			),
			description: trans("Requests that exceeded their due date"),
			value: Number(data?.delayedApprovalsCount) || 0,
			format: (val) => `${val}`,
		},
		{
			key: "rejectionCount",
			title: trans("Rejection Count"),
			icon: `${iconBasePath}rejection-x.svg`,
			iconColor: "#DC2626", // Red
			tooltip: trans("Rejected IRs, MIRs & Submittals"),
			description: trans("total rejected items"),
			value: Number(data?.totalRejected) || 0,
			format: (val) => `${val}`,
		},
		{
			key: "avgQAApprovalTime",
			title: trans("Avg QA Approval Time"),
			icon: `${iconBasePath}checkmark-circle.svg`,
			iconColor: "#059669", // Green
			tooltip: trans(
				"Average time from QA Approval to Consultant Approval (IR)"
			),
			description: trans("Avg. time from QA to Consultant approval"),
			value: Number(data?.avgQAApprovalTime) || 0,
			format: (val) => formatDaysToPretty(val),
		},
		{
			key: "criticalOpenItems",
			title: trans("Critical Open Items"),
			icon: `${iconBasePath}warning-triangle.svg`,
			iconColor: "#F97316", // Orange
			tooltip: trans("Critical items still open in IRs and SNAG lists"),
			description: trans("Critical items still open in IRs and SNAG lists"),
			value: data?.criticalOpenItems || 0,
			format: (val) => `${val}`,
		},
		{
			key: "openDeficiencies",
			title: trans("Open Deficiencies"),
			icon: `${iconBasePath}document-edit.svg`,
			iconColor: "#0077B6", // Blue
			tooltip: trans("Items needing correction (IR)"),
			description: trans("Items needing correction (IR)"),
			value: data.openDeficiencies || 0,
			format: (val) => val,
		},
		{
			key: "totalActiveItems",
			title: trans("Total Active Items"),
			icon: `${iconBasePath}package-box.svg`,
			iconColor: "#7C3AED", // Purple
			tooltip: trans("across all types (IR, SNAG, Submittals)"),
			description: trans("across all types (IR, SNAG, Submittals)"),
			value: data.totalActiveItems || 0,
			format: (val) => val,
		},
		{
			key: "avgConsultantApprovalTime",
			title: trans("Avg Consultant Approval Time"),
			icon: `${iconBasePath}clock-timer.svg`,
			iconColor: "#0891B2", // Teal/Cyan
			tooltip: trans(
				"Average time from contractor submission to QA approval (IR)"
			),
			description: trans("Avg. time from QA to Consultant approval"),
			value: Number(data?.avgConsultantApprovalTime) || 0,
			format: (val) => formatDaysToPretty(val),
		},
		{
			key: "avgCycleTime",
			title: trans("Avg Cycle Time"),
			icon: `${iconBasePath}reload.svg`,
			iconColor: "#EC4899", // Pink/Magenta
			tooltip: trans("From submission to closure"),
			description: trans("From submission to closure"),
			value: Number(data?.avgCycleTime) || 0,
			format: (val) => formatDaysToPretty(val),
		},
	];

	return (
		<Row gutter={[16, 16]}>
			{alertTypes?.map((alert) => {
				const displayValue = alert.format(alert?.value);

				return (
					<Col xs={24} sm={12} lg={6} key={alert.key}>
						<Tooltip title={trans(alert.tooltip)}>
							<div
								style={{
									borderRadius: "12px",
									padding: "16px",
									background: "#F5F7FA",
									display: "flex",
									flexDirection: "column",
									gap: "12px",
									minHeight: "120px",
									cursor: "pointer",
									transition: "all 0.2s ease",
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = "translateY(-2px)";
									e.currentTarget.style.boxShadow =
										"0 4px 12px rgba(0,0,0,0.1)";
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = "translateY(0)";
									e.currentTarget.style.boxShadow = "none";
								}}
							>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										height: "100%",
										width: "100%",
									}}
								>
									{/* LEFT: title + value + desc */}
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "8px",
										}}
									>
										<p
											style={{
												fontSize: "13px",
												fontWeight: 600,
												color: "#42526E",
												margin: 0,
												lineHeight: "1.4",
											}}
										>
											{alert.title}
										</p>

										<span
											style={{
												fontSize: "20px",
												fontWeight: 700,
												color: "#111827",
												lineHeight: "1.2",
											}}
										>
											{displayValue}
										</span>

										<span
											style={{
												fontSize: "12px",
												fontWeight: 400,
												color: "#6B7280",
												lineHeight: "1.4",
											}}
										>
											{alert.description}
										</span>
									</div>

									{/* RIGHT: centered icon */}
									<div
										style={{
											width: "38px",
											height: "38px",
											borderRadius: "10px",
											backgroundColor: alert.iconColor,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											flexShrink: 0,
										}}
									>
										<img
											src={alert.icon}
											alt=""
											style={{
												width: "22px",
												height: "22px",
												filter: "brightness(0) invert(1)",
											}}
											onError={(e) => {
												const img = e.target;
												const parent = img.parentElement;
												img.style.display = "none";
												if (parent) {
													parent.textContent = "•";
													parent.style.color = "white";
													parent.style.fontSize = "26px";
												}
											}}
										/>
									</div>
								</div>
							</div>
						</Tooltip>
					</Col>
				);
			})}
		</Row>
	);
}
